import React from "react";
import {
  Box,
  Container,
  Stack,
  Grid,
  Divider,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import {
  HeadingGradient,
  LinkGradient,
  IconGradient,
  SmallTextGradient,
} from "./Gradient";

interface FooterLink {
  label: string;
  path: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const socialLinks = [
  { Icon: TwitterIcon, href: "https://x.com/itsyourstoryapp", label: "X" },
  {
    Icon: FacebookIcon,
    href: "https://www.facebook.com/profile.php?id=61581378206730",
    label: "Facebook",
  },
  {
    Icon: InstagramIcon,
    href: "https://www.instagram.com/its_your_story.app/",
    label: "Instagram",
  },
  {
    Icon: LinkedInIcon,
    href: "https://www.linkedin.com/company/itsyourstoryapp/",
    label: "LinkedIn",
  },
];

const footerSections: FooterSection[] = [
  {
    title: "Discover",
    links: [
      { label: "Explore", path: "/explore" },
      { label: "Create Story", path: "/create-story" },
      { label: "What's New", path: "/whats-new" },
    ],
  },
  {
    title: "Challenges",
    links: [
      { label: "Ink Masters", path: "/challenges/ink-masters" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", path: "/about" },
      { label: "Careers", path: "/careers" },
      { label: "Contact", path: "/contact" },
    ],
  },
  {
    title: "Legal & Policies",
    links: [
      { label: "Privacy Policy", path: "/privacy-policy" },
      { label: "Terms of Service", path: "/terms-of-service" },
      { label: "Content Guidelines", path: "/content-guidelines" },
      { label: "Cookie Policy", path: "/cookie-policy" },
      { label: "Copyright Policy", path: "/copyright-policy" },
      { label: "Data Rights", path: "/data-rights" },
      { label: "Acceptable Use", path: "/acceptable-use-policy" },
    ],
  },
];

export default function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        pt: { xs: 8, md: 10 },
        pb: { xs: 4, md: 5 },
        px: { xs: 2, sm: 3, md: 4 },
        backgroundColor: "transparent",
        borderTop: (theme) =>
          `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)"
              : "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.1) 50%, transparent 100%)",
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Main Content */}
        <Grid container spacing={{ xs: 4, sm: 5, md: 6 }}>
          {/* Brand & Mission */}
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <HeadingGradient
                variant="h4"
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  fontWeight: 800,
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.25rem" },
                  letterSpacing: "-0.03em",
                  lineHeight: 1.2,
                }}
              >
                It's Your Story
              </HeadingGradient>
              <SmallTextGradient
                variant="body1"
                gradientVariant="subtle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.875rem", sm: "0.938rem", md: "1rem" },
                  lineHeight: { xs: 1.6, sm: 1.8 },
                  maxWidth: { xs: "100%", md: "85%" },
                  display: "block",
                }}
              >
                Share your thoughts, experiences, and ideas with the world.
                Build a community around your unique stories.
              </SmallTextGradient>
            </Box>

            {/* Social Links */}
            <Box>
              <SmallTextGradient
                variant="caption"
                gradientVariant="subtle"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.688rem", sm: "0.75rem" },
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: { xs: 1.5, sm: 2 },
                  display: "block",
                  opacity: 0.7,
                }}
              >
                Connect With Us
              </SmallTextGradient>
              <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} flexWrap="wrap">
                {socialLinks.map(({ Icon, href, label }, i) => (
                  <Box
                    key={i}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 },
                      borderRadius: 3,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(0,0,0,0.02)",
                      border: (theme) =>
                        `1.5px solid ${
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.08)"
                        }`,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      cursor: "pointer",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: (theme) =>
                          theme.palette.mode === "dark"
                            ? "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(165,180,252,0.1) 100%)"
                            : "linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(59,130,246,0.05) 100%)",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                      },
                      "&:hover": {
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.04)",
                        transform: "translateY(-3px) scale(1.05)",
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.2)"
                            : "rgba(0,0,0,0.15)",
                        boxShadow: (theme) =>
                          theme.palette.mode === "dark"
                            ? "0 8px 24px rgba(0,0,0,0.25)"
                            : "0 8px 24px rgba(0,0,0,0.12)",
                        "&::before": {
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <IconGradient gradientVariant="subtle">
                      <Icon sx={{ fontSize: { xs: 18, sm: 22 }, position: "relative", zIndex: 1 }} />
                    </IconGradient>
                  </Box>
                ))}
                <Box
                  component="a"
                  href="mailto:contact@itsyourstory.co.in"
                  aria-label="Email"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 3,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.02)",
                    border: (theme) =>
                      `1.5px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.08)"
                      }`,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: (theme) =>
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(165,180,252,0.1) 100%)"
                          : "linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(59,130,246,0.05) 100%)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                    },
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.04)",
                      transform: "translateY(-3px) scale(1.05)",
                      borderColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.15)",
                      boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                          ? "0 8px 24px rgba(0,0,0,0.25)"
                          : "0 8px 24px rgba(0,0,0,0.12)",
                      "&::before": {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <IconGradient gradientVariant="subtle">
                    <EmailIcon sx={{ fontSize: { xs: 18, sm: 22 }, position: "relative", zIndex: 1 }} />
                  </IconGradient>
                </Box>
              </Stack>
            </Box>
          </Grid>

          {/* Navigation Links */}
          <Grid item xs={12} md={6}>
            <Grid 
              container 
              spacing={{ xs: 3, sm: 3, md: 4 }}
            >
              {footerSections.map((section, index) => {
                // Special handling for Legal & Policies - display links horizontally
                const isLegalSection = section.title === "Legal & Policies";
                
                return (
                  <Grid 
                    item 
                    xs={6}
                    sm={isLegalSection ? 12 : 4}
                    md={isLegalSection ? 12 : 4}
                    key={index}
                  >
                    <HeadingGradient
                      variant="h6"
                      sx={{
                        mb: { xs: 1.5, sm: 2, md: 2.5 },
                        fontWeight: 700,
                        fontSize: { xs: "0.75rem", sm: "0.813rem" },
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                        opacity: 0.95,
                      }}
                    >
                      {section.title}
                    </HeadingGradient>
                    {isLegalSection ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: { xs: 1, sm: 1.5, md: 2 },
                          rowGap: { xs: 1, sm: 1.5 },
                        }}
                      >
                        {section.links.map((link) => (
                          <LinkGradient
                            key={link.path}
                            to={link.path}
                            showUnderlineOnHover={true}
                            underlineHeight={2}
                            sx={{
                              fontSize: { xs: "0.813rem", sm: "0.875rem", md: "0.938rem" },
                              fontWeight: 400,
                              color: "text.secondary",
                              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                              position: "relative",
                              whiteSpace: "nowrap",
                              "&:hover": {
                                color: "text.primary",
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            {link.label}
                          </LinkGradient>
                        ))}
                      </Box>
                    ) : (
                      <Stack spacing={{ xs: 1, sm: 1.5 }}>
                        {section.links.map((link) => (
                          <LinkGradient
                            key={link.path}
                            to={link.path}
                            showUnderlineOnHover={true}
                            underlineHeight={2}
                            sx={{
                              fontSize: { xs: "0.813rem", sm: "0.875rem", md: "0.938rem" },
                              fontWeight: 400,
                              color: "text.secondary",
                              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                              position: "relative",
                              "&:hover": {
                                color: "text.primary",
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            {link.label}
                          </LinkGradient>
                        ))}
                      </Stack>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>

        {/* Divider */}
        <Divider
          sx={{
            my: { xs: 4, sm: 5, md: 6 },
            borderColor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
          }}
        />

        {/* Footer Bottom */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 3 }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <SmallTextGradient
            variant="caption"
            gradientVariant="subtle"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.75rem", sm: "0.813rem" },
              display: "block",
              opacity: 0.75,
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            © {new Date().getFullYear()} It's Your Story. All rights reserved.
          </SmallTextGradient>
          <LinkGradient
            href="mailto:contact@itsyourstory.co.in"
            showUnderlineOnHover={true}
            underlineHeight={2}
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.813rem" },
              fontWeight: 500,
              color: "text.secondary",
              whiteSpace: "nowrap",
              opacity: 0.75,
              transition: "all 0.25s ease",
              textAlign: { xs: "center", sm: "right" },
              "&:hover": {
                color: "text.primary",
                opacity: 1,
                transform: "translateX(2px)",
              },
            }}
          >
            contact@itsyourstory.co.in
          </LinkGradient>
        </Stack>
      </Container>
    </Box>
  );
}
